import React, { useState } from "react"
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Typography,
  Link,
  Paper,
} from "@mui/material"
import ExpandLess from "@mui/icons-material/ExpandLess"
import ExpandMore from "@mui/icons-material/ExpandMore"

const APIDocs: React.FC = () => {
  const [selectedSection, setSelectedSection] = useState<string>("")

  const [openAbout, setOpenAbout] = useState(false)
  const [openTutorials, setOpenTutorials] = useState(false)

  const handleSectionSelect = (section: string) => {
    setSelectedSection(section)
  }

  const handleClickAbout = () => {
    setOpenAbout(!openAbout)
    setSelectedSection("")
  }

  const handleClickTutorials = () => {
    setOpenTutorials(!openTutorials)
    setSelectedSection("")
  }

  const IsQvision = window.location.hostname.includes("qvisiontechnologies")
  const IsClip = window.location.hostname.includes("clip")

  const handleClickPublicAPI = () => {
    return (
      <div>
        <Typography
          variant="h5"
          gutterBottom
        >
          Send Recognitions
        </Typography>
        <Typography
          variant="bodyNormal"
          paragraph
        >
          In order to send a recognition, you must add to the body of the
          request an array of the receiver's emails with the amount of points,
          recognition type and the sender's email, like the following:
        </Typography>
        <Paper
          elevation={3}
          style={{ padding: "20px", color: "black" }}
        >
          <Typography variant="bodyNormal">
            <code>
              Request type: POST <br />
            </code>
          </Typography>
        </Paper>
        <br />
        <Paper
          elevation={3}
          style={{ padding: "20px", color: "black" }}
        >
          <Typography variant="bodyNormal">
            <code>
              endpoint: https://api.motivy.co/send_feedback <br />
            </code>
          </Typography>
        </Paper>
        <br />
        <strong>Request body</strong>
        <br />
        <Paper
          elevation={3}
          style={{ padding: "20px", color: "black" }}
        >
          <Typography variant="bodyNormal">
            <code>
              {"{"} \ <br />
              "sender_email": SENDER'S EMAIL" \ <br />
              "reciever_emails": [RECIEVER'S EMAIL]" \ <br />
              "amount": AMOUNT OF POINTS" \ <br />
              "post_types": [POST TYPES]`" \ <br />
              {"}"}
            </code>
          </Typography>
        </Paper>
        <br />
        <Typography
          variant="h5"
          gutterBottom
        >
          Send Badges
        </Typography>
        <Typography
          variant="bodyNormal"
          paragraph
        >
          In order to send a badge, it must previously be created in your admin
          console you must add to the body of the request an array of the
          receiver's emails and the badge id, like the following:
        </Typography>
        <Paper
          elevation={3}
          style={{ padding: "20px", color: "black" }}
        >
          <Typography variant="bodyNormal">
            <code>
              Request type: POST <br />
            </code>
          </Typography>
        </Paper>
        <br />
        <Paper
          elevation={3}
          style={{ padding: "20px", color: "black" }}
        >
          <Typography variant="bodyNormal">
            <code>
              endpoint: https://api.motivy.co/org/send/recognition/ <br />
            </code>
          </Typography>
        </Paper>
        <br />
        <strong>Request body</strong>
        <br />
        <Paper
          elevation={3}
          style={{ padding: "20px", color: "black" }}
        >
          <Typography variant="bodyNormal">
            <code>
              {"{"} \ <br />
              "reciever_emails": [RECIEVER'S EMAIL]" \ <br />
              "badge_id": BADGE ID" \ <br />
              {"}"}
            </code>
          </Typography>
        </Paper>
        <br />
        <strong>Note!</strong>
        <br />
        <Typography
          variant="bodyNormal"
          paragraph
        >
          All the requests must have the proper authorization header set, which
          is explained in the Authenticate section.
        </Typography>
      </div>
    )
  }
  const getGeneralInformationContent = () => {
    return (
      <div>
        <Typography
          variant="h3"
          gutterBottom
        >
          Save time and money by digitizing your recognition programs with{" "}
          {IsQvision ? "Cooltura" : IsClip ? "Reco" : "Motivy"}
        </Typography>
        <Typography
          variant="bodyNormal"
          paragraph
        >
          Enhance your employee's experience by digitalizing key HR processes
          and use the power of gamified recognitions to revolutionize your
          business culture.
        </Typography>
        <Typography
          variant="h5"
          gutterBottom
        >
          - About us
        </Typography>
        <Typography
          variant="bodyNormal"
          paragraph
        >
          We are a cloud platform that centralizes key HR operations and uses
          the power of recognition to increase productivity.
        </Typography>
        <Typography
          variant="h5"
          gutterBottom
        >
          - How does it work?
        </Typography>
        <Typography
          variant="bodyNormal"
          paragraph
        >
          Send recognitions to your colleagues, redeem points in the catalog,
          participate in corporate programs and track your goals.
        </Typography>
        <Typography
          variant="h5"
          gutterBottom
        >
          - People Insights
        </Typography>
        <Typography
          variant="bodyNormal"
          paragraph
        >
          Our analytical reports will provide you with the appropriate data to
          identify recognition trends and discover hidden skills within your
          employees.
        </Typography>
        <Link
          href="https://www.motivy.co"
          target="_blank"
          rel="noopener"
        >
          For more information, visit here.
        </Link>
      </div>
    )
  }
  const getAPIKeyIntroContent = () => {
    return (
      <div>
        <Typography
          variant="h3"
          gutterBottom
        >
          Introduction to API keys
        </Typography>
        <Typography
          variant="bodyNormal"
          paragraph
        >
          When you use an API key to authenticate to an API, the API key does
          not identify a principal, nor does it provide any authorization
          information. An API key has the following components, which you use to
          manage and use the key:
        </Typography>
        <Typography
          variant="bodyNormal"
          paragraph
        >
          <strong>String</strong>
          <br />
          The API key string is an encrypted string, for example,
          AIzaSyDaGmWKa4JsXZ-HjGw7ISLn_3namBGewQe. When you use an API key to
          authenticate, you always use the key's string. API keys do not have an
          associated JSON file.
        </Typography>

        <Typography
          variant="bodyNormal"
          paragraph
        >
          To manage API keys, you must have a client environment. To access your
          API key dashboard, follow this route: Settings/Admin Panel/Public API.
          There, you will find your public API management console.{" "}
        </Typography>
        <Typography
          variant="bodyNormal"
          paragraph
        >
          <strong>Request</strong>
          <br />
          Every time you forward a request to the public API services, the API
          must be included in the authorization header section of the API
          request, like the following:
        </Typography>
        <Typography
          variant="bodyNormal"
          paragraph
        >
          Here is an example of using cURL to make a POST request with an API
          key:
        </Typography>
        <Paper
          elevation={3}
          style={{ padding: "20px", color: "black" }}
        >
          <Typography variant="bodyNormal">
            <code>
              curl -X POST \ <br />
              -H "Authorization: YOUR_API_KEY" \ <br />
              -H "Content-Type: application/json; charset=utf-8" \ <br />
            </code>
          </Typography>
        </Paper>
        <br />
        <Paper
          elevation={3}
          style={{ padding: "20px", color: "black" }}
        >
          <Typography variant="bodyNormal">
            <code>
              curl -X GET \ <br />
              -H "Authorization: YOUR_API_KEY" \ <br />
              -H "Content-Type: application/json; charset=utf-8" \ <br />
            </code>
          </Typography>
        </Paper>
      </div>
    )
  }

  // To use this content, you can call the getAPIKeyIntroContent function in your component

  const getContent = () => {
    switch (selectedSection) {
      case "general_information":
        return getGeneralInformationContent()
      case "authenticate":
        return getAPIKeyIntroContent()
      case "services":
        return handleClickPublicAPI()
      // Add more cases for other subsections as needed
      default:
        return getGeneralInformationContent()
    }
  }

  return (
    <div style={{ display: "flex" }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: 230,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 230,
            boxSizing: "border-box",
          },
        }}
      >
        <List>
          {/* About Section */}
          <ListItem
            button
            onClick={handleClickAbout}
          >
            <ListItemText primary="About" />
            {openAbout ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse
            in={openAbout}
            timeout="auto"
            unmountOnExit
          >
            <List
              component="div"
              disablePadding
              style={{ paddingLeft: "20px" }}
            >
              <ListItem
                button
                onClick={() => handleSectionSelect("general_information")}
              >
                <ListItemText primary="General Information" />
              </ListItem>
            </List>
          </Collapse>

          {/* Tutorials Section */}
          <ListItem
            button
            onClick={handleClickTutorials}
          >
            <ListItemText primary="How To Guide" />
            {openTutorials ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse
            in={openTutorials}
            timeout="auto"
            unmountOnExit
          >
            <List
              component="div"
              disablePadding
              style={{ paddingLeft: "20px" }}
            >
              <ListItem
                button
                onClick={() => handleSectionSelect("authenticate")}
              >
                <ListItemText primary="Authenticate" />
              </ListItem>
              <ListItem
                button
                onClick={() => handleSectionSelect("services")}
              >
                <ListItemText primary="Public API Services" />
              </ListItem>
            </List>
          </Collapse>
        </List>
      </Drawer>

      {/* Main Content */}
      <div style={{ flex: 1, padding: "20px" }}>{getContent()}</div>
    </div>
  )
}

export default APIDocs
